import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import { useWorkspace } from './WorkspaceContext';
import { toast } from 'sonner';

const StrategyInsightsContext = createContext();

export const useStrategyInsights = () => useContext(StrategyInsightsContext);

export const StrategyInsightsProvider = ({ children }) => {
  const { activeWorkspace } = useWorkspace();
  const [insights, setInsights] = useState([]);
  const [summary, setSummary] = useState({ total: 0, high_priority: 0, recommended: 0, upcoming: 0 });
  const [loading, setLoading] = useState(true);

  const fetchInsights = useCallback(async () => {
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data, error } = await supabase.functions.invoke('generate-strategy-insights', {
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) throw error;

      setInsights(data.insights || []);
      setSummary(data.summary || { total: 0, high_priority: 0, recommended: 0, upcoming: 0 });
    } catch (error) {
      console.error('Error fetching strategy insights:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInsights();

    // Set up realtime subscriptions for relevant tables
    const strategySub = supabase.channel('strategy-insights-strat')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'strategies' }, () => {
        fetchInsights();
      }).subscribe();

    const auditSub = supabase.channel('strategy-insights-audit')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'strategic_audits' }, () => {
        fetchInsights();
      }).subscribe();

    const workspaceSub = supabase.channel('strategy-insights-ws')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'workspaces' }, () => {
        fetchInsights();
      }).subscribe();

    const dismissedSub = supabase.channel('strategy-insights-dismiss')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'dismissed_insights' }, () => {
        fetchInsights();
      }).subscribe();

    return () => {
      supabase.removeChannel(strategySub);
      supabase.removeChannel(auditSub);
      supabase.removeChannel(workspaceSub);
      supabase.removeChannel(dismissedSub);
    };
  }, [fetchInsights]);

  const dismissInsight = async (insightId, insightType, referenceId, durationStr) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const dismissUntil = new Date();
      if (durationStr === '7_days') dismissUntil.setDate(dismissUntil.getDate() + 7);
      else if (durationStr === '30_days') dismissUntil.setDate(dismissUntil.getDate() + 30);
      else if (durationStr === '1_day') dismissUntil.setDate(dismissUntil.getDate() + 1);
      else if (durationStr === 'forever') dismissUntil.setFullYear(dismissUntil.getFullYear() + 100);
      else dismissUntil.setDate(dismissUntil.getDate() + 7); // Default

      const { error } = await supabase.from('dismissed_insights').insert({
        user_id: user.id,
        insight_type: insightType,
        reference_id: referenceId,
        dismissed_until: dismissUntil.toISOString()
      });

      if (error) throw error;

      // Optimistic update
      setInsights(prev => prev.filter(i => i.id !== insightId));
      fetchInsights();
      toast.success("Insight dismissed");
    } catch (e) {
      console.error("Error dismissing insight:", e);
      toast.error("Failed to dismiss insight");
    }
  };

  const value = {
    insights,
    summary,
    loading,
    refreshInsights: fetchInsights,
    dismissInsight
  };

  return (
    <StrategyInsightsContext.Provider value={value}>
      {children}
    </StrategyInsightsContext.Provider>
  );
};
